<template lang="pug">
.injected-product-carousel(v-if="products.length" :class="containerClass")
  ProductCarousel(:products="products" :category="categoryUrl" :title="title")
</template>

<script setup lang="ts">
import { onBeforeMount, onServerPrefetch, ref } from 'vue'
import type { IReflektionProcessedProduct } from '@/js/services/rfk/types'
import { useCategoryStore } from '@/js/stores/categoryStore'
import { useHttpStore } from '@/js/stores/httpStore'
import { useRogue } from '@/js/services/Rogue'

const PRODUCT_COUNT = 12

// RFK's category widget, matching the one ProductList uses when it loads a category itself.
const CATEGORY_WIDGET = 'rfkid_10'

const props = defineProps<{
  categoryId: number,
  // Optional heading from the placeholder. ProductCarousel omits the heading entirely when this is
  // unset, and translates it through the active namespace when it is.
  title?: string,
  // The container the post body is laid out in. The carousel takes the same one so it lines up with
  // the text around it, whatever width that layout uses.
  containerClass?: string,
}>()

const rogue = useRogue()
const categoryStore = useCategoryStore()
const httpStore = useHttpStore()

const cacheKey = `index-carousel-${props.categoryId}`

const products = ref<IReflektionProcessedProduct[]>([])
const categoryUrl = ref<string | undefined>(undefined)

onServerPrefetch(async () => {
  await load()
  httpStore.setCache(cacheKey, { products: products.value, categoryUrl: categoryUrl.value })
})

onBeforeMount(() => {
  const cached = httpStore.cache[cacheKey] as { products: IReflektionProcessedProduct[], categoryUrl?: string } | undefined

  // Reusing the server's products keeps the first client render identical to the SSR markup;
  // refetching here would blank the carousel until the request came back.
  if (cached) {
    products.value = cached.products
    categoryUrl.value = cached.categoryUrl

    return
  }

  load()
})

async function load(): Promise<void> {
  // categoryStore resolves undefined for a category that is missing, deleted, or not carried by
  // this storefront — all cases where the tag should leave no trace in the post.
  const category = await categoryStore.load(props.categoryId, rogue)

  if (!category) {
    rogue.log.warn(`Index product carousel: category ${props.categoryId} is unavailable on ${rogue.site?.code}`)

    return
  }

  categoryUrl.value = category.url
  products.value = await fetchProducts(category.url)
}

async function fetchProducts(url: string): Promise<IReflektionProcessedProduct[]> {
  try {
    const request = rogue.featureFlags.isOn('enable_constructor')
      ? rogue.cnstrc.getBrowseResults('group_id', String(props.categoryId), { numResults: PRODUCT_COUNT })
      : rogue.rfk?.fetch({
        page_number : 1,
        uri         : url,
        n_item      : PRODUCT_COUNT,
        widget      : CATEGORY_WIDGET,
        sort        : { value: [{ type: 'featured', order: 'desc' }] },
      })

    // getBrowseResults answers from cache synchronously; awaiting covers both shapes.
    const response = await request

    return response?.content?.product?.value || []
  } catch (e) {
    rogue.log.warn(`Index product carousel: unable to load products for category ${props.categoryId}`, e as Error)

    return []
  }
}
</script>

<style lang="scss" scoped>
.injected-product-carousel {
  margin-bottom: 32px;
}
</style>
